import React from 'react';

const CSDHelpPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-partrunner-black rounded-xl p-6 mb-8">
          <h1 className="text-2xl font-bold text-partrunner-yellow">
            ¿Cómo generar el Certificado de Sello Digital (CSD)?
          </h1>
          <p className="text-gray-300 mt-2">
            Guía paso a paso para tramitar tu CSD ante el SAT
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          {/* Intro */}
          <div className="bg-yellow-50 border-l-4 border-partrunner-yellow p-4 rounded-r-lg">
            <p className="text-sm text-gray-700">
              El <strong>Certificado de Sello Digital (CSD)</strong> es un archivo digital emitido por el SAT que permite firmar 
              electrónicamente tus facturas para garantizar su autenticidad y validez fiscal. Funciona como una 
              "firma electrónica avanzada" específica para comprobantes fiscales, distinta de la e.firma. 
              Es un <strong>requisito indispensable</strong> para facturar en línea y tiene una <strong>vigencia de 4 años</strong>.
            </p>
          </div>

          {/* Requisitos */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Antes de iniciar necesitas:</h2>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-partrunner-yellow font-bold mt-0.5">1.</span>
                <span>Contar con tu <strong>e.firma (antes FIEL)</strong> vigente — archivos .CER y .KEY de tu firma electrónica.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-partrunner-yellow font-bold mt-0.5">2.</span>
                <span>
                  Tener instalada la última versión de <strong>Java</strong>:{' '}
                  <a href="https://www.java.com/es/download/" target="_blank" rel="noopener noreferrer" 
                     className="text-blue-600 hover:underline">
                    https://www.java.com/es/download/
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-partrunner-yellow font-bold mt-0.5">3.</span>
                <span>
                  Crear una carpeta llamada <strong>"CERTIFICADO DE SELLO DIGITAL"</strong> en el escritorio de tu computadora 
                  donde se guardarán todos los archivos generados.
                </span>
              </li>
            </ul>
          </div>

          <hr className="border-gray-200" />

          {/* Paso 1 */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-partrunner-black text-partrunner-yellow text-lg font-bold rounded-full w-10 h-10 flex items-center justify-center">
                1
              </span>
              <h2 className="text-xl font-bold text-gray-900">
                Generar archivos .KEY y .SDG con CERTIFICA
              </h2>
            </div>

            <ol className="space-y-4 text-sm text-gray-700 ml-4">
              <li>
                <p>
                  <strong>Descarga el programa CERTIFICA</strong> (antes llamado SOLCEDI) desde la página oficial del SAT:{' '}
                  <a href="https://portalsat.plataforma.sat.gob.mx/certifica/" target="_blank" rel="noopener noreferrer"
                     className="text-blue-600 hover:underline break-all">
                    https://portalsat.plataforma.sat.gob.mx/certifica/
                  </a>
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  <p className="text-xs text-blue-700">
                    💡 Selecciona la versión que corresponda a tu sistema operativo: <strong>32 bits o 64 bits</strong>.
                    Para archivos .RAR o .ZIP, da clic derecho → "Abrir con Java(TM) Platform SE binary".
                  </p>
                </div>
              </li>

              <li>
                <p>
                  <strong>Ejecuta el programa CERTIFICA.</strong> Al iniciar aparecerá un menú de opciones. 
                  Selecciona <strong>"Solicitud de Certificado de Sello Digital"</strong>.
                </p>
              </li>

              <li>
                <p>
                  Presiona en <strong>"Seleccionar Archivo"</strong>. Elige el archivo <strong>.CER de tu e.firma</strong> y presiona en "Siguiente".
                </p>
              </li>

              <li>
                <p>
                  El sistema cargará los datos de tu RFC y razón social. Ingresa:
                </p>
                <ul className="mt-2 ml-4 space-y-1 list-disc">
                  <li><strong>Nombre de sucursal o unidad</strong> (si no tienes sucursales, puedes escribir "Unidad" o tu nombre).</li>
                  <li><strong>Contraseña</strong> para la llave privada del CSD.</li>
                </ul>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                  <p className="text-xs text-red-700">
                    ⚠️ <strong>¡Importante!</strong> Recuerda esta contraseña — es la que necesitarás para subir tu CSD 
                    a Sube tu Factura.
                  </p>
                </div>
                <p className="mt-2">Presiona en <strong>"Agregar"</strong> y luego en <strong>"Siguiente"</strong>.</p>
              </li>

              <li>
                <p>
                  <strong>Mueve el cursor del mouse</strong> en diferentes direcciones hasta que la barra de progreso se complete al 100%. 
                  Mantén el cursor moviéndose sin hacer clic, puede tardar unos minutos.
                </p>
              </li>

              <li>
                <p>
                  Presiona <strong>"Seleccionar Archivo"</strong> y selecciona el archivo <strong>.KEY de tu e.firma</strong>. 
                  Ingresa la contraseña de tu clave privada y presiona <strong>"Firmar y Guardar"</strong>.
                </p>
              </li>

              <li>
                <p>
                  Selecciona la carpeta <strong>"CERTIFICADO DE SELLO DIGITAL"</strong> que creaste anteriormente para 
                  guardar los archivos generados. Presiona <strong>"Guardar"</strong> y luego <strong>"Terminar"</strong>.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                  <p className="text-xs text-green-700">
                    ✅ Al concluir deberás tener dos archivos nuevos:
                    <br />• <strong>Archivo .KEY</strong> del Certificado de Sello Digital (CSD)
                    <br />• <strong>Archivo .SDG</strong> (requerimiento para descargar el .CER en el SAT)
                  </p>
                </div>
              </li>
            </ol>
          </div>

          <hr className="border-gray-200" />

          {/* Paso 2 */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-partrunner-black text-partrunner-yellow text-lg font-bold rounded-full w-10 h-10 flex items-center justify-center">
                2
              </span>
              <h2 className="text-xl font-bold text-gray-900">
                Obtener el archivo .CER en el portal del SAT
              </h2>
            </div>

            <ol className="space-y-4 text-sm text-gray-700 ml-4">
              <li>
                <p>
                  <strong>Accede al portal CertiSAT</strong> con tu e.firma:{' '}
                  <a href="https://aplicacionesc.mat.sat.gob.mx/certisat/" target="_blank" rel="noopener noreferrer"
                     className="text-blue-600 hover:underline break-all">
                    https://aplicacionesc.mat.sat.gob.mx/certisat/
                  </a>
                </p>
              </li>

              <li>
                <p>
                  Selecciona la opción <strong>"Envío de solicitud de Certificado de Sello Digital"</strong>.
                </p>
              </li>

              <li>
                <p>
                  Da clic en <strong>"Examinar"</strong>. Selecciona el archivo <strong>.SDG</strong> que generaste 
                  en el paso anterior y haz clic en <strong>"Abrir"</strong>.
                </p>
              </li>

              <li>
                <p>
                  <strong>Envía el requerimiento</strong> para obtener el Certificado de Sello Digital (archivo .CER). 
                  El SAT te proporcionará un <strong>número de operación</strong> — guárdalo.
                </p>
              </li>

              <li>
                <p>
                  <strong>Espera unos minutos</strong> para que se active el certificado. Luego ingresa nuevamente a CertiSAT 
                  y selecciona <strong>"Recuperación de Certificados"</strong>.
                </p>
              </li>

              <li>
                <p>
                  Ingresa el <strong>número de operación</strong> que obtuviste y haz clic en <strong>"Seguimiento"</strong>. 
                  Descarga el <strong>"Acuse de recibo"</strong>.
                </p>
              </li>

              <li>
                <p>
                  Regresa a la página principal de CertiSAT y entra nuevamente a <strong>"Recuperación de certificados"</strong>. 
                  Ingresa tu <strong>RFC</strong> y selecciona el archivo tipo <strong>SELLO</strong> para descargar el archivo <strong>.CER</strong>.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                  <p className="text-xs text-green-700">
                    ✅ ¡Listo! Ahora tienes los 3 archivos de tu CSD:
                    <br />• <strong>.CER</strong> — Certificado de Sello Digital
                    <br />• <strong>.KEY</strong> — Llave privada del CSD
                    <br />• <strong>Contraseña</strong> que asignaste en el Paso 1
                  </p>
                </div>
              </li>
            </ol>
          </div>

          <hr className="border-gray-200" />

          {/* Notas importantes */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <h3 className="font-bold text-amber-800 mb-3">📋 Notas importantes</h3>
            <ul className="space-y-2 text-sm text-amber-900">
              <li className="flex items-start gap-2">
                <span>⏳</span>
                <span>
                  Una vez generados tus archivos del CSD, debes esperar <strong>de 24 a 72 horas</strong> para que 
                  el SAT te agregue a la LCO (Lista de Contribuyentes Obligados) y puedas comenzar a facturar.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span>📁</span>
                <span>
                  <strong>No confundas los archivos:</strong> Los archivos .CER y .KEY de tu e.firma son DIFERENTES 
                  a los del CSD. Guárdalos en carpetas separadas.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span>🔒</span>
                <span>
                  El CSD tiene una <strong>vigencia de 4 años</strong>. Antes de que expire, deberás renovarlo 
                  repitiendo este proceso.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span>🔑</span>
                <span>
                  Para subir tu CSD a <strong>Sube tu Factura</strong>, necesitarás los 3 archivos: 
                  el .CER del CSD, el .KEY del CSD y la contraseña que asignaste.
                </span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="text-center pt-4">
            <p className="text-sm text-gray-500 mb-3">
              ¿Ya tienes tu CSD? Súbelo en tu cuenta para empezar a facturar.
            </p>
            <a 
              href="/portal/dashboard" 
              className="inline-block bg-partrunner-black text-partrunner-yellow font-semibold px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Ir a mi cuenta →
            </a>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Fuente: SAT — Servicio de Administración Tributaria •{' '}
          <a href="https://www.sat.gob.mx" target="_blank" rel="noopener noreferrer" className="hover:underline">
            www.sat.gob.mx
          </a>
        </p>
      </div>
    </div>
  );
};

export default CSDHelpPage;
